import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import type { SvgIconType } from "@/shared/public-ui/SvgIcon";

type HeroButtonContent = {
  label: string;
  href: string;
  icon?: GenericIconType;
  tone: "primary";
};

type HowItWorksCardContent = {
  title: string;
  description: string;
  icon: SvgIconType;
  color: "primary" | "secondary" | "tertiary";
};

type FeatureCardContent = {
  title: string;
  description: string;
  icon: SvgIconType;
  iconBg1: string;
  iconBg2: string;
};

export const homePageContent = {
  hero: {
    title: {
      text: "Profile",
      accent: "Yourself",
    },
    subtitles: [
      "Build a maintained career profile through four clear perspectives.",
      "Understand what you can do, what you know, and where you thrive.",
      "Turn past experience into profile data you can refine and reuse.",
      "Connect skills, knowledge, preferences, and self-description.",
      "Shape profile language from real experience instead of copying it.",
      "Prepare selected profile data for people, systems, and agents.",
      "Create professional context that stays useful beyond one document.",
      "Profile yourself with continuity, interpretation, and control.",
    ],
    buttons: [
      {
        label: "Profile Yourself",
        href: "/register",
        icon: "log_in",
        tone: "primary",
      },
    ],
  },
  howItWorks: {
    title: "How it works",
    kicker:
      "Turn any digital material and career moments into a comprehensive profile easy to maintain over time.",
    cards: [
      {
        title: "Collect profile material",
        description:
          "Add CV text, notes, transcripts, drafts, stories, or manual career moments that help recall past experience and future aspirations.",
        icon: "desktop",
        color: "primary",
      },
      {
        title: "Confirm what belongs",
        description:
          "Review suggested thoughts, keep what is true, and anchor important statements to the situations that give them context.",
        icon: "presentation",
        color: "secondary",
      },
      {
        title: "Shape a maintained profile",
        description:
          "Reflect on confirmed thoughts, view them through profile perspectives, and keep selected material ready for future use.",
        icon: "cube",
        color: "tertiary",
      },
    ] satisfies HowItWorksCardContent[],
  },
  features: {
    title: "What you get",
    kicker:
      "One profile that supports a spectrum of deliveries, from static exports to reusable system integration.",
    cards: [
      {
        title: "Profile export",
        description:
          "Create bounded outputs such as CVs, summaries, or selected profile packages when you need a clear static version.",
        icon: "record",
        iconBg1: "moveon.primaryLight",
        iconBg2: "moveon.primary",
      },
      {
        title: "Adaptive memory",
        description:
          "Prepare reusable professional context that agents and systems can draw from without reducing you to one generated document.",
        icon: "analyse",
        iconBg1: "moveon.secondaryLight",
        iconBg2: "moveon.secondary",
      },
      {
        title: "Sharing with others",
        description:
          "Share profile data with readers, organizations, collaborators, or services while keeping control over the content.",
        icon: "resume",
        iconBg1: "moveon.tertiaryLight",
        iconBg2: "moveon.tertiary",
      },
    ] satisfies FeatureCardContent[],
  },
  pricing: {
    title: "One profile workspace",
    kicker: {
      text: "Start with a CV, enter profile material manually, or import from other systems. For details,",
      linkText: "see the FAQ.",
      linkHref: "/faq",
    },
    badge: "Lifetime Access",
    price: "0,-",
    currency: "in any currency",
    benefits: [
      {
        icon: "confirm",
        strong: "Unlimited",
        text: "source materials, career situations and thoughts",
      },
    ] satisfies {
      icon: GenericIconType;
      strong: string;
      text: string;
    }[],
  },
} as const satisfies {
  hero: {
    title: {
      text: string;
      accent: string;
    };
    subtitles: readonly [string, ...string[]];
    buttons: readonly HeroButtonContent[];
  };
  howItWorks: {
    title: string;
    kicker: string;
    cards: readonly HowItWorksCardContent[];
  };
  features: {
    title: string;
    kicker: string;
    cards: readonly FeatureCardContent[];
  };
  pricing: {
    title: string;
    kicker: {
      text: string;
      linkText: string;
      linkHref: string;
    };
    badge: string;
    price: string;
    currency: string;
    benefits: readonly {
      icon: GenericIconType;
      strong: string;
      text: string;
    }[];
  };
};
